import { alpha, Box, Button, IconButton, Link, styled } from '@mui/material';
import { Link as RouterLink } from 'react-router';

import { LINKS, SIZES } from '../../../../assets/constants/constants.ts';
import Dots from '../../../../assets/images/dots_doodle.svg?styled';
import { LANG } from '../../../../services/Config.js';
import Logo from '../../../Logo/index.ts';

import bankID from './_components/bankid.svg';
import eiendomsverdi from './_components/eiendomsverdi.svg';
import gjeldsregisteret from './_components/gjeldsregisteret.svg';
import FacebookIcon from './_components/icons/FacebookIcon.tsx';
import TiktokIcon from './_components/icons/TiktokIcon.tsx';
import trustpilot from './_components/trustpilot.svg';

const copyrightYear = new Date().getFullYear();

// Styled Components

const FooterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 4, 0),
  width: '100%',
  maxWidth: SIZES.layout.width.xl,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: '35px',
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(8, 4),
    paddingBottom: theme.spacing(0),
    flexDirection: 'row',
    marginTop: 0,
  },
  [theme.breakpoints.up(1560)]: {
    padding: theme.spacing(8, 0),
    paddingBottom: theme.spacing(0),
    margin: theme.spacing(0, 4),
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '35px',
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  zIndex: 1050,
  margin: theme.spacing(6, 'auto', 6),
  backgroundColor: '#FFFFFF',
  color: theme.palette.text.default,
  boxShadow: theme.shadows[21],
  borderRadius: '24px',
  width: '100%',
  padding: theme.spacing(3),
  maxWidth: 330,
  [theme.breakpoints.up('lg')]: {
    margin: 0,
    order: 2,
    height: 'min-content',
  },
}));

const ContactContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  margin: theme.spacing(1.5, 0, 3),
  justifyContent: 'center',
}));

const HalfCircle = styled(Box)(({ theme }) => ({
  width: 54,
  height: 54,
  marginLeft: -27,
  marginRight: theme.spacing(1.5),
  border: `15px solid ${theme.palette.primary.light}`,
  borderRadius: '50%',
  borderBottomColor: 'transparent',
  borderLeftColor: 'transparent',
  transform: 'rotate(-315deg)',
}));

const ContactLinks = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const MainSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
  paddingRight: theme.spacing(2),
  order: 1,
  textAlign: 'left',
}));

const MainColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '50%',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '65%',
  },
}));

const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const StyledLogo = styled(Logo)(({ theme }) => ({
  height: 24,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    marginBottom: theme.spacing(3),
  },
}));

const LeftTopSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(4.5),
}));

const LeftEcoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RightColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RightEcoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const AddressColumn = styled(Box)(({ theme }) => ({
  width: '50%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    width: '30%',
  },
}));

const EcoAddressColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(6),
}));

const TermsLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: theme.spacing(6, 0, 0, 0),
  [theme.breakpoints.up(820)]: {
    display: 'none',
  },
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  backgroundColor: '#0C2346',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  height: 70,
  [theme.breakpoints.up('sm')]: {
    height: 90,
  },
}));

const FooterBottomContent = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 4),
  maxWidth: SIZES.layout.width.xl,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up(820)]: {
    justifyContent: 'space-between',
  },
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up(820)]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const Dot = styled(Box)(({ theme }) => ({
  backgroundColor: '#354560',
  height: 4,
  width: 4,
  margin: theme.spacing(3),
}));

const StyledDots = styled(Dots)(({ theme }) => ({
  position: 'absolute',
  height: 38,
  width: 38,
  left: '50%',
  top: -24,
  transform: 'translate(-50%, -50%) rotate(-40deg)',
  fill: theme.palette.primary.light,
  '& path': { stroke: theme.palette.primary.light },
  [theme.breakpoints.up('lg')]: {
    height: 30,
    width: 30,
    left: 'unset',
    right: -27,
    transform: 'none',
    fill: theme.palette.neutral.dark,
    '& path': { stroke: theme.palette.neutral.dark },
  },
}));

const Footer = () => {
  const { appStrings, routes } = LANG;

  const mainLinks = [
    { name: 'PRODUCTS_REFINANCING', bold: true },
    { name: 'MY_SCOOPR', bold: true },
    { name: 'CUSTOMER_SERVICE', bold: true },
  ] as const;
  const economyLinks = [
    { name: 'MY_ECONOMY', bold: true },
    { name: 'MY_DEBT', bold: false },
    { name: 'CREDIT_CHECK_MYSELF', bold: false },
    // { name: 'PARTNER_BANKS', bold: false },
    { name: 'DEBT_REGISTER', bold: false },
    { name: 'DEBT_INFORMATION', bold: false },
    { name: 'INTEREST_RATE', bold: false },
    { name: 'SMALL_LOAN', bold: false },
    { name: 'DEBT_FREE', bold: false },
    { name: 'CREDIT_CARD_LOAN', bold: false },
    { name: 'DEBT_OVERVIEW', bold: false },
    { name: 'REPAYMENT_PERIOD', bold: false },
    { name: 'REFINANCING_LOANS', bold: false },
    { name: 'SAVE_OR_REPAY_LOAN', bold: false },
    { name: 'MICROLOAN', bold: false },
    { name: 'NOMINAL_AND_EFFECTIVE_INTEREST_RATE', bold: false },
    { name: 'PROPERTY_VALUE', bold: false },
  ] as const;

  const renderLink = (url: string, title: string, bold: boolean) => {
    return (
      <Link
        component={RouterLink}
        to={url}
        sx={(theme) => ({
          color: bold ? theme.palette.text.footerLink : theme.palette.text.contrast,
          marginBottom: theme.spacing(2),
        })}
        underline="hover"
        key={title}
      >
        {bold ? (
          <h4 className="typography-v1-h4">{title}</h4>
        ) : (
          <p className="typography-v1-body3">{title}</p>
        )}
      </Link>
    );
  };

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        width: '100%',
        backgroundColor: theme.palette.background.contrast,
        color: theme.palette.text.contrast,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        isolation: 'isolate',
      })}
    >
      <FooterContent>
        <ContactSection>
          <StyledDots />
          <h2 className="typography-v1-h2">{appStrings.CONTACT.TITLE}</h2>

          <ContactContainer>
            <HalfCircle />
            <ContactLinks>
              <Link href={`tel:${appStrings.CONTACT.PHONE2}`} underline="hover">
                <h4 className="typography-v1-h4">{appStrings.CONTACT.PHONE1}</h4>
              </Link>
              <Link href={`mailto:${appStrings.CONTACT.EMAIL}`} underline="hover">
                <h4 className="typography-v1-h4">{appStrings.CONTACT.EMAIL}</h4>
              </Link>
            </ContactLinks>
          </ContactContainer>

          <h4 className="typography-v1-h4">{appStrings.CONTACT.OPENING_TITLE}</h4>
          <Box
            component="table"
            sx={(theme) => ({
              width: '100%',
              borderSpacing: 0,
              margin: theme.spacing(1, 0, 3),
              '& td': {
                padding: theme.spacing(0.5, 0),
                fontSize: '0.875rem',
              },
              [theme.breakpoints.up('sm')]: {
                '& td': {
                  fontSize: '1rem',
                },
              },
            })}
          >
            <tbody>
              {appStrings.CONTACT.OPENING.REGULAR.items.map(({ day, hours }) => (
                <tr key={day}>
                  <td>
                    <p className="typography-v1-body3">{day}:</p>
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      minWidth: 80,
                      verticalAlign: 'bottom',
                    }}
                  >
                    <h4 className="typography-v1-h4">{hours}</h4>
                  </td>
                </tr>
              ))}

              {appStrings.CONTACT.OPENING.HOLIDAYS.display ? (
                <>
                  <tr>
                    <td>
                      <p className="pt-2 typography-v1-h4">
                        {appStrings.CONTACT.OPENING.HOLIDAYS.title}
                      </p>
                    </td>
                  </tr>
                  {appStrings.CONTACT.OPENING.HOLIDAYS.items.map(({ day, hours }) => (
                    <tr key={day}>
                      <td>
                        <p className="typography-v1-body3">{day}:</p>
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          minWidth: 80,
                          verticalAlign: 'bottom',
                        }}
                      >
                        <h4 className="typography-v1-h4">{hours}</h4>
                      </td>
                    </tr>
                  ))}
                </>
              ) : null}
            </tbody>
          </Box>

          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={routes.CUSTOMER_SERVICE}
            sx={{ margin: 'auto' }}
          >
            <p className="typography-v1-button2">{appStrings.MENU.CUSTOMER_SERVICE}</p>
          </Button>
        </ContactSection>

        <MainSection>
          <MainColumn>
            <LeftColumn>
              <StyledLogo />
              <LeftTopSection>
                {mainLinks.map(({ name, bold }) =>
                  renderLink(routes[name], appStrings.MENU[name], bold),
                )}
              </LeftTopSection>
              <LeftEcoSection>
                {economyLinks
                  .slice(0, 6)
                  .map(({ name, bold }) => renderLink(routes[name], appStrings.MENU[name], bold))}
              </LeftEcoSection>
            </LeftColumn>

            <RightColumn>
              <RightEcoSection>
                {economyLinks
                  .slice(6, economyLinks.length - 3)
                  .map(({ name, bold }) => renderLink(routes[name], appStrings.MENU[name], bold))}
              </RightEcoSection>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <EcoAddressColumn>
                  {economyLinks
                    .slice(-3)
                    .map(({ name, bold }) => renderLink(routes[name], appStrings.MENU[name], bold))}
                </EcoAddressColumn>
              </Box>
            </RightColumn>
          </MainColumn>
          <AddressColumn>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <EcoAddressColumn>
                {economyLinks
                  .slice(-3)
                  .map(({ name, bold }) => renderLink(routes[name], appStrings.MENU[name], bold))}
              </EcoAddressColumn>
            </Box>
            <Box component="address">
              <p className="typography-v1-body3">Scoopr AS</p>
              <p className="mb-4 typography-v1-body4">Org.nr. 952 660 225</p>
              <p className="typography-v1-body4">Karenslyst Allé 2</p>
              <p className="mb-4 typography-v1-body4">0278 Oslo</p>
              <IconButton
                component={Link}
                href={LINKS.FACEBOOK}
                rel="noopener noreferrer"
                aria-label={appStrings.CONTACT.FACEBOOK_LABEL}
                size="large"
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  margin: theme.spacing(0, 1, 0, -1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                  },
                  '&:focus-visible': {
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                  },
                  '& svg': {
                    width: 30,
                    height: 30,
                  },
                  [theme.breakpoints.down('md')]: {
                    '& svg': {
                      width: 24,
                      height: 24,
                    },
                  },
                })}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                component={Link}
                href={LINKS.TIKTOK}
                rel="noopener noreferrer"
                aria-label={appStrings.CONTACT.TIKTOK_LABEL}
                size="large"
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  margin: theme.spacing(0, 1, 0, -1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                  },
                  '&:focus-visible': {
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                  },
                  '& svg': {
                    width: 30,
                    height: 30,
                  },
                  [theme.breakpoints.down('md')]: {
                    '& svg': {
                      width: 24,
                      height: 24,
                    },
                  },
                })}
              >
                <TiktokIcon />
              </IconButton>
            </Box>
            <TermsLinksContainer>
              <Link
                component={RouterLink}
                to={routes.TERMS_OF_USE_MIN_SCOOPR}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <p className="typography-v1-body3">{appStrings.MENU.TERMS_OF_USE}</p>
              </Link>
              <Link
                component={RouterLink}
                to={routes.PRIVACY_STATEMENT}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <p className="typography-v1-body3">{appStrings.MENU.PRIVACY}</p>
              </Link>
              <Link
                component={RouterLink}
                to={routes.COOKIES}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <p className="typography-v1-body3">{appStrings.MENU.COOKIES}</p>
              </Link>
            </TermsLinksContainer>
          </AddressColumn>
        </MainSection>
      </FooterContent>

      <div
        className="mb-8 w-full max-[1560px]:px-8 min-[1560px]:mx-8"
        style={{ maxWidth: SIZES.layout.width.xl }}
      >
        <div className="mx-auto mb-4 flex flex-row flex-wrap items-center gap-x-8 gap-y-6 sm:gap-x-12">
          <img
            src={gjeldsregisteret}
            loading="lazy"
            alt="Gjeldsregisteret"
            width={177}
            height={42}
            className="w-auto max-md:h-8"
          />
          <img
            src={bankID}
            alt="BankID"
            loading="lazy"
            width={134}
            height={20}
            className="w-auto max-md:h-4"
          />
          <img
            src={eiendomsverdi}
            loading="lazy"
            alt="Eiendomsverdi"
            width={215}
            height={22}
            className="w-auto max-md:h-4"
          />
          <img
            src={trustpilot}
            loading="lazy"
            alt="Trustpilot"
            width={113}
            height={48}
            className="w-auto max-md:h-10"
          />
        </div>
        <div className="typography-v1-body3">
          Scoopr AS er låneformidler med konsesjon fra{' '}
          <a
            href="https://www.finanstilsynet.no/virksomhetsregisteret/detalj/?id=109620"
            target="_blank"
            className="underline"
          >
            Finanstilsynet
          </a>
        </div>
      </div>

      <FooterBottom>
        <FooterBottomContent>
          <FooterLinks>
            <Link
              component={RouterLink}
              to={routes.TERMS_OF_USE_MIN_SCOOPR}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <p className="typography-v1-body3">{appStrings.MENU.TERMS_OF_USE}</p>
            </Link>
            <Dot />
            <Link
              component={RouterLink}
              to={routes.PRIVACY_STATEMENT}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <p className="typography-v1-body3">{appStrings.MENU.PRIVACY_STATEMENT}</p>
            </Link>
            <Dot />
            <Link
              component={RouterLink}
              to={routes.COOKIES}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <p className="typography-v1-body3">{appStrings.MENU.COOKIES}</p>
            </Link>
          </FooterLinks>

          <p className="typography-v1-body3">
            {copyrightYear} {appStrings.RIGHTS}
          </p>
        </FooterBottomContent>
      </FooterBottom>
    </Box>
  );
};

export default Footer;
